import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  VideoOff,
  MicOff,
  Phone,
  MessageSquare,
  Clock,
  Shield,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  AlertTriangle,
  Camera,
  Volume2
} from 'lucide-react';
import { useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';

interface GeneralParticipantSessionProps {
  roomId?: string;
  clientName?: string;
  requestId?: string;
  onLeaveSession?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'agent' | 'customer' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

// Generate token using the working debug endpoint (same as universal template)
const generateToken = async (roomId: string, participantName: string, isHost: boolean = false, maxRetries: number = 3) => {
  console.log(`[General] Generating token for room ${roomId}, participant ${participantName}, host: ${isHost}`);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use the same working debug endpoint as universal template
      const endpoint = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token';
      const body = { meetingId: roomId, participantName, isHost };
      
      console.log(`[General] Using debug endpoint: ${endpoint}`, body);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Token generation failed: ${response.status}`);
      }

      const tokenData = await response.json();
      console.log(`[General] Token generated successfully for ${participantName}${attempt > 1 ? ` (attempt ${attempt})` : ''}`, tokenData);
      
      return tokenData.token;
    } catch (error) {
      if (attempt === maxRetries) {
        console.error('[General] Token generation error (final attempt):', error);
        throw error;
      }
      
      console.warn(`[General] Token generation attempt ${attempt} failed, retrying...`, error);
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
};

// Inner component that uses RealtimeKit hooks
const RealtimeKitMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  clientName: string;
  roomId: string;
}> = ({ authToken, onEndCall, clientName, roomId }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Welcome to your session. An agent will join shortly.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize RealtimeKit meeting
  useEffect(() => {
    const initializeSession = async () => {
      try {
        console.log('[GeneralParticipant] Initializing meeting with token');
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[GeneralParticipant] Meeting initialized successfully');
        
        // Add system message when client joins
        const joinMessage: ChatMessage = {
          id: Date.now().toString(),
          sender: 'system',
          message: 'You have joined the session.',
          timestamp: new Date(),
          type: 'system'
        };
        setChatMessages(prev => [...prev, joinMessage]);
      } catch (error) {
        console.error('[GeneralParticipant] Failed to initialize meeting:', error);
      }
    };

    if (authToken) {
      initializeSession();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [authToken, initMeeting]);

  // Timer for session duration
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Format session time
  const formatTime = useCallback((seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  // Handle chat message send
  const sendMessage = useCallback(() => {
    if (newMessage.trim()) {
      const message: ChatMessage = {
        id: Date.now().toString(),
        sender: 'customer',
        message: newMessage.trim(),
        timestamp: new Date(),
        type: 'text'
      };
      setChatMessages(prev => [...prev, message]);
      setNewMessage('');
    }
  }, [newMessage]);

  // Handle leave session
  const handleLeaveSession = useCallback(() => {
    console.log('[GeneralParticipant] Leaving session');
    // Leave the meeting properly
    if (meeting) {
      meeting.leave();
      console.log('[GeneralParticipant] Meeting.leave() called');
    }
    // Navigate back after a short delay
    setTimeout(() => {
      onEndCall();
    }, 500);
  }, [meeting, onEndCall]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Joining session...</p>
          <p className="text-sm text-gray-400 mt-2">Please wait while we connect you</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold">Customer Session</h1>
            <div className="flex items-center space-x-2 text-green-400">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Connected</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-300">
              <Clock className="w-4 h-4" />
              <span className="font-mono text-sm">{formatTime(sessionTime)}</span>
            </div>
            
            <button
              onClick={() => setShowChat(!showChat)}
              aria-label={showChat ? 'Hide chat' : 'Show chat'}
              className={`p-2 rounded transition-colors ${
                showChat ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          <div className="h-full w-full relative">
            {meeting ? (
              <RtkMeeting 
                meeting={meeting} 
                showSetupScreen={false}
                mode="fill"
              />
            ) : (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Connecting to session...</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Floating End Call Button */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <button
              onClick={handleLeaveSession}
              aria-label="Leave session"
              className="bg-red-600 text-white px-6 py-3 rounded-full hover:bg-red-700 transition-colors flex items-center space-x-2 shadow-lg"
            >
              <Phone className="w-5 h-5" />
              <span>Leave Session</span>
            </button>
          </div>

          {/* Session Info Overlay */}
          <div className="absolute top-4 left-4 bg-gray-800 bg-opacity-90 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-sm">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-gray-300">Secure Session</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Your session is private and secure</p>
          </div>
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-medium">Chat</h3>
              <p className="text-sm text-gray-400">Communicate with your agent</p>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`${msg.sender === 'system' ? 'text-center' : ''}`}>
                  {msg.sender === 'system' ? (
                    <div className="text-xs text-gray-500 bg-gray-700 rounded px-2 py-1 inline-block">
                      {msg.message}
                    </div>
                  ) : (
                    <div className={`${msg.sender === 'customer' ? 'text-right' : 'text-left'}`}>
                      <div className={`inline-block px-3 py-2 rounded-lg max-w-xs ${
                        msg.sender === 'customer' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-700 text-white'
                      }`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className="text-xs opacity-75 mt-1">
                          {msg.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            
            {/* Chat Input */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendMessage}
                  aria-label="Send message"
                  className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gray-800 border-t border-gray-700 p-3 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4 text-gray-400">
            <div className="flex items-center space-x-1">
              <Camera className="w-4 h-4" />
              <span>Camera</span>
            </div>
            <div className="flex items-center space-x-1">
              <Volume2 className="w-4 h-4" />
              <span>Audio</span>
            </div>
          </div>
          
          <div className="text-gray-500 text-xs">
            Having issues? Contact support
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component with token generation
export const GeneralParticipantSession: React.FC<GeneralParticipantSessionProps> = ({
  roomId: initialRoomId,
  clientName = 'Customer',
  requestId,
  onLeaveSession
}) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const clientNameFromUrl = urlParams.get('clientName') || urlParams.get('participantName') || clientName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';

  useEffect(() => {
    const initializeToken = async () => {
      if (!roomId) {
        setError('No room ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('[GeneralParticipant] Generating token for room:', roomId);
        const token = await generateToken(roomId, clientNameFromUrl, false);
        setAuthToken(token);
        setLoading(false);
        console.log('[GeneralParticipant] Token generated successfully');
      } catch (err) {
        console.error('[GeneralParticipant] Failed to generate token:', err);
        setError('Failed to join session. Please try again.');
        setLoading(false);
      }
    };

    initializeToken();
  }, [roomId, clientNameFromUrl]);

  const handleEndCall = useCallback(() => {
    console.log('[GeneralParticipant] Session ended');
    if (onLeaveSession) {
      onLeaveSession();
    } else {
      // Navigate back to a thank you page or close window
      alert('Thank you for using our service. You may now close this window.');
      window.close();
    }
  }, [onLeaveSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Joining session...</p>
          <p className="text-sm text-gray-400 mt-2">Room: {roomId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white max-w-md">
          <div className="bg-red-600 rounded-full p-4 mx-auto mb-4 w-16 h-16 flex items-center justify-center">
            <XCircle className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Failed to Join Session</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!authToken) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p>Preparing session...</p>
        </div>
      </div>
    );
  }

  return (
    <RealtimeKitMeetingComponent
      authToken={authToken}
      onEndCall={handleEndCall}
      clientName={clientNameFromUrl}
      roomId={roomId}
    />
  );
};
